import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';
import { useClients, useProjects } from '@/features/tasks/useTasks';

interface ClientTimerPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (clientId: number, projectId: number | null) => void;
}

/**
 * Compact two-step picker: tap a client, optionally a project, done.
 * Prefer name chips over selects/sheets, fewer taps mid-sprint.
 */
export function ClientTimerPicker({ open, onClose, onSelect }: ClientTimerPickerProps) {
  const clients = useClients();
  const [clientId, setClientId] = useState<number | null>(null);
  const projects = useProjects(clientId ?? undefined);
  const onSelectRef = useRef(onSelect);
  const onCloseRef = useRef(onClose);
  onSelectRef.current = onSelect;
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) setClientId(null);
  }, [open]);

  // No projects → start immediately after client pick.
  useEffect(() => {
    if (clientId == null || projects.isLoading || projects.isFetching) return;
    if ((projects.data ?? []).length === 0) {
      onSelectRef.current(clientId, null);
      onCloseRef.current();
    }
  }, [clientId, projects.data, projects.isLoading, projects.isFetching]);

  if (!open) return null;

  const selectedClient = clients.data?.find((c) => c.id === clientId);
  const projectList = projects.data ?? [];
  const pickingProject =
    clientId != null && !projects.isLoading && projectList.length > 0;

  const start = (cid: number, pid: number | null) => {
    onSelect(cid, pid);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Dismiss"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Start client timer"
        className="relative z-10 w-full max-w-sm rounded-xl border border-border bg-surface-1 p-4 shadow-lg"
      >
        {!pickingProject ? (
          <>
            <p className="mb-3 text-small font-medium text-text-secondary">Client</p>
            <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto">
              {(clients.data ?? []).map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className={cn(
                      'pressable w-full rounded-lg px-3 py-2.5 text-left text-body',
                      'hover:bg-surface-2',
                    )}
                    onClick={() => setClientId(c.id)}
                  >
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>
            {(clients.data ?? []).length === 0 && (
              <p className="text-small text-text-tertiary">No clients yet.</p>
            )}
            {clientId != null && projects.isLoading && (
              <p className="mt-2 text-small text-text-tertiary">Starting…</p>
            )}
          </>
        ) : (
          <>
            <button
              type="button"
              className="mb-2 text-label text-accent"
              onClick={() => setClientId(null)}
            >
              ← {selectedClient?.name ?? 'Client'}
            </button>
            <p className="mb-3 text-small font-medium text-text-secondary">
              Project <span className="font-normal text-text-tertiary">(optional)</span>
            </p>
            <ul className="flex max-h-56 flex-col gap-1 overflow-y-auto">
              <li>
                <Button className="w-full justify-start" onClick={() => start(clientId, null)}>
                  Start without project
                </Button>
              </li>
              {projectList.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className={cn(
                      'pressable w-full rounded-lg px-3 py-2.5 text-left text-body',
                      'hover:bg-surface-2',
                    )}
                    onClick={() => start(clientId, p.id)}
                  >
                    {p.name}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
        <Button variant="ghost" className="mt-3 w-full" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
